/**
 * Chat state as a pure reducer.
 *
 * Split out from the hook so streaming behaviour — the part with all the
 * ordering subtleties — can be tested by replaying event sequences without a
 * DOM or a network.
 */

import type { ChatStreamEvent, Message } from "../../shared/types.ts";
import { dropAnimation, spliceAnimation } from "../../shared/anim-splice.ts";
import { replacePlotBlock } from "../../shared/plot-splice.ts";
import type {
  AgentId,
  AgentState,
  FollowUp,
  LessonPlan,
  Review,
} from "../../shared/agents.ts";
import { AGENT_IDS } from "../../shared/agents.ts";

export interface StreamingMessage {
  readonly id: string;
  readonly content: string;
  readonly reasoning: string;
  readonly plan: LessonPlan | null;
  readonly review: Review | null;
  readonly followUps: readonly FollowUp[] | null;
  readonly agents: Readonly<Record<AgentId, AgentState>>;
  readonly agentDetail: Readonly<Partial<Record<AgentId, string>>>;
}

export interface ChatState {
  readonly conversationId: string | null;
  readonly title: string | null;
  readonly messages: readonly Message[];
  readonly streaming: StreamingMessage | null;
  readonly error: string | null;
  readonly phase: "idle" | "sending" | "streaming";
}

export type ChatAction =
  | { readonly type: "load"; readonly conversationId: string | null; readonly messages: readonly Message[] }
  | { readonly type: "submit"; readonly content: string }
  | { readonly type: "event"; readonly event: ChatStreamEvent }
  | { readonly type: "settled" }
  | { readonly type: "dismissError" };

export const initialChatState: ChatState = {
  conversationId: null,
  title: null,
  messages: [],
  streaming: null,
  error: null,
  phase: "idle",
};

const idleAgents = (): Record<AgentId, AgentState> =>
  Object.fromEntries(AGENT_IDS.map((id) => [id, "pending"])) as Record<
    AgentId,
    AgentState
  >;

const OPTIMISTIC_ID = "pending-user-message";

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "load":
      return {
        ...initialChatState,
        conversationId: action.conversationId,
        messages: action.messages,
      };

    case "submit":
      // Shown immediately; the server's `start` event swaps in the real id.
      return {
        ...state,
        error: null,
        phase: "sending",
        messages: [
          ...state.messages,
          {
            id: OPTIMISTIC_ID,
            conversationId: state.conversationId ?? "",
            role: "user",
            content: action.content,
            reasoning: null,
            model: null,
            plan: null,
            review: null,
            followUps: null,
            createdAt: new Date().toISOString(),
          },
        ],
      };

    case "event":
      return applyEvent(state, action.event);

    case "settled":
      // The stream ended without a `done` (tab slept, socket dropped): keep
      // whatever text arrived rather than discarding a partial derivation.
      return state.streaming ? commitStreaming(state) : { ...state, phase: "idle" };

    case "dismissError":
      return { ...state, error: null };
  }
}

function applyEvent(state: ChatState, event: ChatStreamEvent): ChatState {
  switch (event.type) {
    case "start":
      return {
        ...state,
        conversationId: event.conversationId,
        phase: "streaming",
        messages: state.messages.map((message) =>
          message.id === OPTIMISTIC_ID
            ? { ...message, id: event.userMessageId, conversationId: event.conversationId }
            : message,
        ),
        streaming: {
          id: event.assistantMessageId,
          content: "",
          reasoning: "",
          plan: null,
          review: null,
          followUps: null,
          agents: idleAgents(),
          agentDetail: {},
        },
      };

    case "agent":
      if (!state.streaming) return state;
      return {
        ...state,
        streaming: {
          ...state.streaming,
          agents: { ...state.streaming.agents, [event.agent]: event.state },
          agentDetail:
            event.detail === undefined
              ? state.streaming.agentDetail
              : { ...state.streaming.agentDetail, [event.agent]: event.detail },
        },
      };

    case "plan":
      if (!state.streaming) return state;
      return { ...state, streaming: { ...state.streaming, plan: event.plan } };

    case "content":
      if (!state.streaming) return state;
      return {
        ...state,
        streaming: {
          ...state.streaming,
          content: state.streaming.content + event.delta,
        },
      };

    case "reasoning":
      if (!state.streaming) return state;
      return {
        ...state,
        streaming: {
          ...state.streaming,
          reasoning: state.streaming.reasoning + event.delta,
        },
      };

    case "review":
      if (!state.streaming) return state;
      return { ...state, streaming: { ...state.streaming, review: event.review } };

    case "followups":
      if (!state.streaming) return state;
      return {
        ...state,
        streaming: { ...state.streaming, followUps: event.followUps },
      };

    /**
     * The finished sketch, arriving well after the prose it belongs to. The
     * same splice runs on the server before persisting, so a reader who
     * refreshes sees exactly what a reader who waited sees.
     */
    case "anim":
      if (!state.streaming) return state;
      return {
        ...state,
        streaming: {
          ...state.streaming,
          content: spliceAnimation(
            state.streaming.content,
            event.brief,
            event.code,
          ),
        },
      };

    /** No sketch survived review. Take the placeholder away rather than
        leaving the reader watching a spinner that will never resolve. */
    case "anim-dropped":
      if (!state.streaming) return state;
      return {
        ...state,
        streaming: {
          ...state.streaming,
          content: dropAnimation(state.streaming.content, event.brief),
        },
      };

    /** A figure the doctor repaired, or removed because it would not draw. */
    case "plot-repair":
      if (!state.streaming) return state;
      return {
        ...state,
        streaming: {
          ...state.streaming,
          content: replacePlotBlock(
            state.streaming.content,
            event.original,
            event.fixed,
          ),
        },
      };

    case "title":
      return { ...state, title: event.title };

    case "done":
      return state.streaming ? commitStreaming(state) : { ...state, phase: "idle" };

    case "error":
      // Partial text is still worth keeping — the error renders beneath it.
      return {
        ...(state.streaming ? commitStreaming(state) : state),
        error: event.message,
        phase: "idle",
      };
  }
}

/** Fold the in-flight message into the permanent list. */
function commitStreaming(state: ChatState): ChatState {
  const streaming = state.streaming;
  if (!streaming) return { ...state, phase: "idle" };

  // Nothing arrived at all — drop the placeholder instead of showing a blank.
  if (streaming.content.trim().length === 0) {
    return { ...state, streaming: null, phase: "idle" };
  }

  const message: Message = {
    id: streaming.id,
    conversationId: state.conversationId ?? "",
    role: "assistant",
    content: streaming.content,
    reasoning: streaming.reasoning.length > 0 ? streaming.reasoning : null,
    model: null,
    plan: streaming.plan,
    review: streaming.review,
    followUps: streaming.followUps,
    createdAt: new Date().toISOString(),
  };

  return {
    ...state,
    messages: [...state.messages, message],
    streaming: null,
    phase: "idle",
  };
}
